import { apiUrls } from "./ApiUrls";
import { BaseApiService } from "./BaseApiService";
import { ApiResponse } from "./ApiResponse";
import {
  UserProfile,
  UserEducation,
  UserSkill,
  WorkExperience,
  UserProfileRequest,
  UserEducationRequest,
  UserSkillRequest,
  WorkExperienceRequest,
} from "./types/userprofile.types";
import { Address, Organization } from "./types";
import { MentorPreference } from "../pages/mentor/types";
import {
  MentorPreferenceResponse,
  MentorPreferences,
  MentorPreferencesRequest,
} from "./types/preferences";

/**
 * Service class for user profile-related API operations
 * Connects to the backend UserProfileController endpoints
 */
class UserProfileService extends BaseApiService {
  private static instance: UserProfileService;

  public static getInstance(): UserProfileService {
    if (!UserProfileService.instance) {
      UserProfileService.instance = new UserProfileService();
    }
    return UserProfileService.instance;
  }

  /**
   * Get current user's personal information
   */
  static async getPersonalInfo(): Promise<ApiResponse<UserProfile>> {
    try {
      console.log("👤 [UserProfileService] Fetching personal info...");
      const response = await this.getAuthenticated(
        apiUrls.userProfile.personalInfo.get,
        "Get Personal Info"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching personal info:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch personal info",
      };
    }
  }

  /** Recompute profile completion % on the server (students). */
  static async recalculateProfileCompletion(): Promise<ApiResponse<number>> {
    try {
      const response = await this.postAuthenticated(
        apiUrls.userProfile.personalInfo.recalculateCompletion,
        {},
        "Recalculate Profile Completion"
      );
      return this.normalizeCompletionPercentResponse(response);
    } catch (error: any) {
      return {
        status: "error",
        message: error.message || "Failed to recalculate profile completion",
      };
    }
  }

  /** Persist client-calculated profile completion % (students). */
  static async syncProfileCompletionPercent(
    profileCompletionPercent: number
  ): Promise<ApiResponse<number>> {
    try {
      const response = await this.postAuthenticated(
        apiUrls.userProfile.personalInfo.syncCompletion,
        { profileCompletionPercent: Math.round(profileCompletionPercent) },
        "Sync Profile Completion"
      );
      return this.normalizeCompletionPercentResponse(response);
    } catch (error: any) {
      return {
        status: "error",
        message: error.message || "Failed to sync profile completion",
      };
    }
  }

  private static normalizeCompletionPercentResponse(
    response: ApiResponse<unknown>
  ): ApiResponse<number> {
    const raw = response?.data;
    const percent =
      typeof raw === "number"
        ? raw
        : typeof raw === "object" && raw != null
          ? Number(
              (raw as Record<string, unknown>).profileCompletionPercent ??
                (raw as Record<string, unknown>).profile_completion_percent
            )
          : NaN;
    if (Number.isFinite(percent)) {
      return { ...response, data: Math.round(percent) };
    }
    return response as ApiResponse<number>;
  }

  /**
   * Update current user's personal information
   */
  static async updatePersonalInfo(
    data: UserProfileRequest
  ): Promise<ApiResponse<UserProfile>> {
    try {
      console.log("✏️ [UserProfileService] Updating personal info:", data);
      const response = await this.postAuthenticated(
        apiUrls.userProfile.personalInfo.update,
        data,
        "Update Personal Info"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error updating personal info:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to update personal info",
      };
    }
  }

  /**
   * Upload profile picture (PNG / JPEG) for the current user.
   */
  static async uploadProfilePicture(
    file: File
  ): Promise<ApiResponse<UserProfile>> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await this.postMultipartAuthenticated(
        apiUrls.userProfile.personalInfo.profilePictureUpload,
        formData,
        "Upload Profile Picture"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error uploading profile picture:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to upload profile picture",
        errorCode: error.errorCode,
      };
    }
  }

  static async addOrUpdateAddress(
    data: Address,
    addressType: string
  ): Promise<ApiResponse<Address>> {
    try {
      console.log("✏️ [UserProfileService] Updating address:", data);
      data.addressType = addressType;
      const response = await this.postAuthenticated(
        apiUrls.userProfile.address.addOrUpdate,
        data,
        "Update Address"
      );
      return response;
    } catch (error: any) {
      console.error("❌ [UserProfileService] Error updating address:", error);
      return {
        status: "error",
        message: error.message || "Failed to update address",
      };
    }
  }

  /**
   * Get current user's education history
   */
  static async getEducationList(): Promise<ApiResponse<UserEducation[]>> {
    try {
      console.log("📚 [UserProfileService] Fetching education history...");
      const response = await this.getAuthenticated(
        apiUrls.userProfile.education.list,
        "Get Education History"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching education history:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch education history",
      };
    }
  }

  /**
   * Get user education by user ID (admin only)
   */
  static async getUserEducationById(
    userUniqueId: string
  ): Promise<ApiResponse<UserEducation[]>> {
    try {
      console.log(
        "📚 [UserProfileService] Fetching user education by ID:",
        userUniqueId
      );
      const response = await this.getAuthenticated(
        apiUrls.userProfile.education.getById(userUniqueId),
        "Get User Education by ID"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching user education by ID:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch user education",
      };
    }
  }

  /**
   * Add new education entry
   */
  static async addEducation(
    data: UserEducationRequest
  ): Promise<ApiResponse<UserEducation>> {
    try {
      console.log(
        "➕ [UserProfileService] Adding education entry:",
        data.course
      );
      const response = await this.postAuthenticated(
        apiUrls.userProfile.education.add,
        data,
        "Add Education Entry"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error adding education entry:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to add education entry",
      };
    }
  }

  static async updateEducation(
    data: UserEducationRequest
  ): Promise<ApiResponse<UserEducation>> {
    try {
      console.log(
        "✏️ [UserProfileService] Updating education entry:",
        data.course
      );
      const response = await this.postAuthenticated(
        apiUrls.userProfile.education.update,
        data,
        "Update Education Entry"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error updating education entry:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to update education entry",
      };
    }
  }

  /**
   * Get current Mentor's education history
   */
  static async getMentorEducationList(): Promise<ApiResponse<UserEducation[]>> {
    try {
      console.log(
        "📚 [UserProfileService] Fetching Mentor education history..."
      );
      const response = await this.getAuthenticated(
        apiUrls.mentorProfile.education.list,
        "Get Mentor Education History"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching Mentor education history:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch Mentor education history",
      };
    }
  }

  /**
   * Get user education by user ID (admin only)
   */
  static async getMentorEducationById(
    userUniqueId: string
  ): Promise<ApiResponse<UserEducation[]>> {
    try {
      console.log(
        "📚 [UserProfileService] Fetching Mentor education by ID:",
        userUniqueId
      );
      const response = await this.getAuthenticated(
        apiUrls.mentorProfile.education.getById(userUniqueId),
        "Get Mentor Education by ID"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching MEntor education by ID:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch Mentor education",
      };
    }
  }

  /**
   * Add new Mentor education entry
   */
  static async addMentorEducation(
    data: UserEducationRequest
  ): Promise<ApiResponse<UserEducation>> {
    try {
      console.log(
        "➕ [UserProfileService] Adding education entry for Mentor:",
        data.course
      );
      const response = await this.postAuthenticated(
        apiUrls.mentorProfile.education.add,
        data,
        "Add Education Entry"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error adding education entry for Mentor::",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to add education entry for Mentor:",
      };
    }
  }

  // Update Mentor Education

  static async updateMentorEducation(
    data: UserEducationRequest
  ): Promise<ApiResponse<UserEducation>> {
    try {
      console.log(
        "✏️ [UserProfileService] Mentor Updating education entry:",
        data.course
      );
      const response = await this.postAuthenticated(
        apiUrls.mentorProfile.education.update,
        data,
        "Update Education Entry for Mentor"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error updating education entry for Mentor:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to update education entry for Mentor",
      };
    }
  }

  /**
   * Delete Mentor education entry
   */
  static async deleteMentorEducation(
    educationId: number
  ): Promise<ApiResponse<UserEducation>> {
    try {
      console.log(
        "🗑️ [UserProfileService] Deleting education entry for Mentor:",
        educationId
      );
      const response = await this.postAuthenticated(
        apiUrls.mentorProfile.education.delete(educationId),
        {},
        "Delete Education Entry for Mentor"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error deleting education entry for Mentor:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to delete education entry for Mentor",
      };
    }
  }

  /**
   * Delete education entry
   */
  static async deleteEducation(
    educationId: number
  ): Promise<ApiResponse<UserEducation>> {
    try {
      console.log(
        "🗑️ [UserProfileService] Deleting education entry:",
        educationId
      );
      const response = await this.postAuthenticated(
        apiUrls.userProfile.education.delete(educationId),
        {},
        "Delete Education Entry"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error deleting education entry:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to delete education entry",
      };
    }
  }

  /**
   * Get current user's skills
   */
  static async getUserSkills(): Promise<ApiResponse<UserSkill[]>> {
    try {
      console.log("💡 [UserProfileService] Fetching skills...");
      const response = await this.getAuthenticated(
        apiUrls.userProfile.userskills.list,
        "Get User Skills"
      );
      return response;
    } catch (error: any) {
      console.error("❌ [UserProfileService] Error fetching skills:", error);
      return {
        status: "error",
        message: error.message || "Failed to fetch skills",
      };
    }
  }

  /**
   * Add new skill
   */
  static async addUserSkill(
    data: UserSkillRequest
  ): Promise<ApiResponse<UserSkill>> {
    try {
      console.log("➕ [UserProfileService] Adding skill:", data.skillName);
      const response = await this.postAuthenticated(
        apiUrls.userProfile.userskills.add,
        data,
        "Add User Skill"
      );
      return response;
    } catch (error: any) {
      console.error("❌ [UserProfileService] Error adding skill:", error);
      return {
        status: "error",
        message: error.message || "Failed to add skill",
      };
    }
  }
  /**
   * Add new skill
   */
  static async updateUserSkill(
    data: UserSkillRequest
  ): Promise<ApiResponse<UserSkill>> {
    try {
      console.log("➕ [UserProfileService] Updating skill:", data.skillName);
      const response = await this.postAuthenticated(
        apiUrls.userProfile.userskills.update,
        data,
        "update User Skill"
      );
      return response;
    } catch (error: any) {
      console.error("❌ [UserProfileService] Error updating skill:", error);
      return {
        status: "error",
        message: error.message || "Failed to update skill",
      };
    }
  }

  /**
   * Get user skills by user ID (admin only)
   */
  static async getUserSkillsById(
    userUniqueId: string
  ): Promise<ApiResponse<UserSkill[]>> {
    try {
      console.log(
        "💡 [UserProfileService] Fetching user skills by ID:",
        userUniqueId
      );
      const response = await this.getAuthenticated(
        apiUrls.userProfile.userskills.getById(userUniqueId),
        "Get User Skills by ID"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching user skills by ID:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch user skills",
      };
    }
  }

  /**
   * Delete skill
   */
  static async deleteUserSkill(
    skillId: number
  ): Promise<ApiResponse<UserSkill>> {
    try {
      console.log("🗑️ [UserProfileService] Deleting skill:", skillId);
      const response = await this.postAuthenticated(
        apiUrls.userProfile.userskills.delete(skillId),
        {},
        "Delete Skill"
      );
      return response;
    } catch (error: any) {
      console.error("❌ [UserProfileService] Error deleting skill:", error);
      return {
        status: "error",
        message: error.message || "Failed to delete skill",
      };
    }
  }

  /**
   * Get current user's work experience
   */
  static async getMentorWorkExperience(): Promise<
    ApiResponse<WorkExperience[]>
  > {
    try {
      console.log(
        "💼 [UserProfileService] Fetching Mentor's work experience..."
      );
      const response = await this.getAuthenticated(
        apiUrls.mentorProfile.workExperience.list,
        "Get Mentor's Work Experience"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching mentor work experience:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch Mentor's work experience",
      };
    }
  }

  /**
   * Get user work experience by user ID (admin only)
   */
  static async getMentorWorkExperienceById(
    userUniqueId: string
  ): Promise<ApiResponse<WorkExperience[]>> {
    try {
      console.log(
        "💼 [UserProfileService] Fetching Mentor work experience by ID:",
        userUniqueId
      );
      const response = await this.getAuthenticated(
        apiUrls.mentorProfile.workExperience.getById(userUniqueId),
        "Get User Work Experience by ID"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching Mentor work experience by ID:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch Mentor work experience",
      };
    }
  }

  /**
   * Add new work experience
   */
  static async addMentorWorkExperience(
    data: WorkExperienceRequest
  ): Promise<ApiResponse<WorkExperience>> {
    try {
      console.log(
        "➕ [UserProfileService] Adding work experience:",
        data.jobTitle
      );
      const response = await this.postAuthenticated(
        apiUrls.mentorProfile.workExperience.add,
        data,
        "Add Mentor Work Experience"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error adding Mentor work experience:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to add Mentor work experience",
      };
    }
  }

  /**
   * Update work experience
   */
  static async updateMentorWorkExperience(
    data: WorkExperienceRequest
  ): Promise<ApiResponse<WorkExperience>> {
    try {
      console.log(
        "✏️ [UserProfileService] Updating work experience:",
        data.jobTitle
      );
      const response = await this.postAuthenticated(
        apiUrls.mentorProfile.workExperience.update,
        data,
        "Update Mentor Work Experience"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error updating mentor work experience:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to update mentor work experience",
      };
    }
  }

  /**
   * Delete work experience
   */
  static async deleteMentorWorkExperience(
    workExperienceId: number
  ): Promise<ApiResponse<WorkExperience>> {
    try {
      console.log(
        "🗑️ [UserProfileService] Deleting mentor work experience:",
        workExperienceId
      );
      const response = await this.postAuthenticated(
        apiUrls.mentorProfile.workExperience.delete(workExperienceId),
        {},
        "Delete Mentor Work Experience"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error deleting mentor work experience:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to delete Mentor work experience",
      };
    }
  }

  /**
   * Get current user's work experience
   */
  static async getWorkExperience(): Promise<ApiResponse<WorkExperience[]>> {
    try {
      console.log("💼 [UserProfileService] Fetching work experience...");
      const response = await this.getAuthenticated(
        apiUrls.userProfile.workExperience.list,
        "Get Work Experience"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching work experience:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch work experience",
      };
    }
  }

  /**
   * Get user work experience by user ID (admin only)
   */
  static async getUserWorkExperienceById(
    userUniqueId: string
  ): Promise<ApiResponse<WorkExperience[]>> {
    try {
      console.log(
        "💼 [UserProfileService] Fetching user work experience by ID:",
        userUniqueId
      );
      const response = await this.getAuthenticated(
        apiUrls.userProfile.workExperience.getById(userUniqueId),
        "Get User Work Experience by ID"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching user work experience by ID:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch user work experience",
      };
    }
  }

  /**
   * Add new work experience
   */
  static async addWorkExperience(
    data: WorkExperienceRequest
  ): Promise<ApiResponse<WorkExperience>> {
    try {
      console.log(
        "➕ [UserProfileService] Adding work experience:",
        data.jobTitle
      );
      const response = await this.postAuthenticated(
        apiUrls.userProfile.workExperience.add,
        data,
        "Add Work Experience"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error adding work experience:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to add work experience",
      };
    }
  }

  /**
   * Update work experience
   */
  static async updateWorkExperience(
    data: WorkExperienceRequest
  ): Promise<ApiResponse<WorkExperience>> {
    try {
      console.log(
        "✏️ [UserProfileService] Updating work experience:",
        data.jobTitle
      );
      const response = await this.postAuthenticated(
        apiUrls.userProfile.workExperience.update,
        data,
        "Update Work Experience"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error updating work experience:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to update work experience",
      };
    }
  }

  /**
   * Delete work experience
   */
  static async deleteWorkExperience(
    workExperienceId: number
  ): Promise<ApiResponse<WorkExperience>> {
    try {
      console.log(
        "🗑️ [UserProfileService] Deleting work experience:",
        workExperienceId
      );
      const response = await this.postAuthenticated(
        apiUrls.userProfile.workExperience.delete(workExperienceId),
        {},
        "Delete Work Experience"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error deleting work experience:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to delete work experience",
      };
    }
  }

  /**
   * Get user profile by user ID (admin only)
   */
  static async getUserProfileById(
    userUniqueId: string
  ): Promise<ApiResponse<UserProfile>> {
    try {
      console.log(
        "👤 [UserProfileService] Fetching user profile by ID:",
        userUniqueId
      );
      const response = await this.getAuthenticated(
        apiUrls.userProfile.personalInfo.getById(userUniqueId),
        "Get User Profile by ID"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching user profile by ID:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch user profile",
      };
    }
  }

  /**
   * Get complete student profile for an application
   * @param applicationId The application ID
   */
  static async getApplicationStudentProfile(
    applicationId: string
  ): Promise<ApiResponse<any>> {
    try {
      console.log(
        "👤 [UserProfileService] Fetching application student profile:",
        applicationId
      );
      const response = await this.getAuthenticated(
        apiUrls.organization.applicationStudentProfile(applicationId),
        "Get Application Student Profile"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching application student profile:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch application student profile",
      };
    }
  }

  /**
   * Update user profile by user ID (admin only)
   */
  static async updateUserProfileById(
    userUniqueId: string,
    data: UserProfileRequest
  ): Promise<ApiResponse<UserProfile>> {
    try {
      console.log(
        "✏️ [UserProfileService] Updating user profile by ID:",
        userUniqueId
      );
      const response = await this.postAuthenticated(
        apiUrls.userProfile.personalInfo.updateById(userUniqueId),
        data,
        "Update User Profile by ID"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error updating user profile by ID:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to update user profile",
      };
    }
  }

  /**
   * Get current organization profile
   */
  static async getOrganizationProfile(): Promise<ApiResponse<Organization>> {
    try {
      const response = await this.getAuthenticated(
        apiUrls.userProfile.organization.get,
        "Get Organization Profile"
      );
      return response;
    } catch (error: any) {
      return {
        status: "error",
        message: error.message || "Failed to fetch organization profile",
      };
    }
  }

  /**
   * Update current organization profile
   */
  static async updateOrganizationProfile(data: any): Promise<ApiResponse<any>> {
    try {
      const response = await this.putAuthenticated(
        apiUrls.userProfile.organization.update,
        data,
        "Update Organization Profile"
      );
      return response;
    } catch (error: any) {
      return {
        status: "error",
        message: error.message || "Failed to update organization profile",
        errorCode: error.errorCode,
      };
    }
  }

  /**
   * Update organization address
   */
  static async updateOrganizationAddress(data: any): Promise<ApiResponse<any>> {
    try {
      const response = await this.putAuthenticated(
        apiUrls.userProfile.organization.updateAddress,
        data,
        "Update Organization Address"
      );
      return response;
    } catch (error: any) {
      return {
        status: "error",
        message: error.message || "Failed to update organization address",
      };
    }
  }

  /**
   * Get current user's preference history
   */
  static async getPreferenceList(): Promise<ApiResponse<MentorPreference[]>> {
    try {
      console.log("📚 [UserProfileService] Fetching preference history...");
      const response = await this.getAuthenticated(
        apiUrls.mentorProfile.preference.list,
        "Get Preference History"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching preference history:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch preference history",
      };
    }
  }

  /**
   * Get current user's inactive preference history
   */
  static async getInactivePreferenceList(): Promise<ApiResponse<MentorPreference[]>> {
    try {
      console.log("📚 [UserProfileService] Fetching inactive preference history...");
      const response = await this.getAuthenticated(
        apiUrls.mentorProfile.preference.listInactive,
        "Get Inactive Preference History"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error fetching inactive preference history:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to fetch inactive preference history",
      };
    }
  }

  // Update Mentor Preference

  static async updateMentorPreference(
    data: MentorPreferences
  ): Promise<ApiResponse<MentorPreferences>> {
    try {
      console.log(
        "✏️ [UserProfileService] Mentor Updating Preference entry:",
        data
      );
      data = {
        ...data,
        pastExperience: 3
      };
      const response = await this.putAuthenticated(
        apiUrls.mentorProfile.preference.update,
        data,
        "Update Preference Entry for Mentor"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error updating preference entry for Mentor:",
        error
      );
      return {
        status: "error",
        message:
          error.message || "Failed to update preference entry for Mentor",
      };
    }
  }

  /**
   * Add new Mentor preference entry
   */
  static async addMentorPreference(
    data: MentorPreferencesRequest
  ): Promise<ApiResponse<MentorPreferenceResponse>> {
    try {
      console.log(
        "➕ [UserProfileService] Adding Preference entry for Mentor:",
        data
      );
      data = {
        ...data,
        pastExperience: 3
      };
      const response = await this.postAuthenticated(
        apiUrls.mentorProfile.preference.add,
        data,
        "Add Preference Entry"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error adding Preference entry for Mentor::",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to add Preference entry for Mentor:",
      };
    }
  }

  /**
   * Delete Mentor education entry
   */
  static async deleteMentorPreference(
    preferenceId: string
  ): Promise<ApiResponse<MentorPreferences>> {
    try {
      console.log(
        "🗑️ [UserProfileService] Deleting education entry for Mentor:",
        preferenceId
      );
      const response = await this.postAuthenticated(
        apiUrls.mentorProfile.preference.delete(),
        { preferenceId: preferenceId },
        "Delete Preference Entry for Mentor"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error deleting preference entry for Mentor:",
        error
      );
      return {
        status: "error",
        message:
          error.message || "Failed to delete preference entry for Mentor",
      };
    }
  }

  /**
   * Activate Mentor preference
   */
  static async activateMentorPreference(
    preferenceId: string
  ): Promise<ApiResponse<MentorPreferences>> {
    try {
      console.log(
        "✅ [UserProfileService] Activating preference:",
        preferenceId
      );
      const response = await this.postAuthenticated(
        apiUrls.mentorProfile.preference.activate,
        { preferenceId: preferenceId },
        "Activate Mentor Preference"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error activating preference:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to activate preference",
      };
    }
  }

  /**
   * Deactivate Mentor preference
   */
  static async deactivateMentorPreference(
    preferenceId: string
  ): Promise<ApiResponse<MentorPreferences>> {
    try {
      console.log(
        "⏸️ [UserProfileService] Deactivating preference:",
        preferenceId
      );
      const response = await this.postAuthenticated(
        apiUrls.mentorProfile.preference.deactivate,
        { preferenceId: preferenceId },
        "Deactivate Mentor Preference"
      );
      return response;
    } catch (error: any) {
      console.error(
        "❌ [UserProfileService] Error deactivating preference:",
        error
      );
      return {
        status: "error",
        message: error.message || "Failed to deactivate preference",
      };
    }
  }
}

// Export the class for static method access
export { UserProfileService };
